import type { WCategoryView } from '@zola/svc-web-api-ts-client';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import {
  PrimaryNavIdentifier,
  SecondaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { isUserPostWedding } from '@zola-helpers/client/dist/es/util/dateUtils';

import { ShopHeader } from './ShopHeader';
import { NAV_DATA } from '../../../../components/common/header/UnifiedNavV2';
import { isNewArrivalCollection } from '../../constants';

const { NEW_ARRIVALS, COLLECTIONS, BRANDS } = NAV_DATA.SECONDARY.REGISTRY;

type HeaderProps = {
  aboveBannerContent: React.ReactNode;
  metaData?: {
    title?: string;
    description?: string;
  };
  categoryFilter: WCategoryView[] | null;
  weddingNavKey: string;
  showBanner?: boolean;
  collectionKey?: string;
  brandKey?: string;
  isBaby?: boolean;
  userContext: UserContext | null;
  isPostAuthNav3Shown?: boolean;
};

type ShopPrimaryLink =
  | PrimaryNavIdentifier.WEDDING_BOUTIQUE
  | PrimaryNavIdentifier.REGISTRY
  | PrimaryNavIdentifier.SHOP;

type GetNavPropsResult = {
  activePrimaryLink: ShopPrimaryLink;
  activeSecondaryLink: SecondaryNavIdentifier | undefined;
  activeTertiaryLink: SecondaryNavIdentifier | undefined;
};
/**
 * Gets active primary/secondary/tertiary links for a shop page, including:
 * - Category Landing Page (/shop/category/kitchen)
 * - Collections Landing Page  (/shop/collection/:collectionKey)
 * - Featured Brands LP (/shop/brands/:brandsKey)
 * - Partner Boutique LP (/shop/partner-boutique/:brandKey)
 * - Shop Home (/shop/index)
 * - Shop Search (/shop/gifts
 *
 * Some of the pages can be rendered in a baby context, or as a boutique shopping
 * experience.
 *
 * The primary link will be one of:
 * - WEDDING_BOUTIQUE
 * - REGISTRY
 * - SHOP
 */
export const getNavProps = ({
  weddingNavKey,
  categoryFilter,
  collectionKey,
  brandKey,
  isBaby,
  userContext,
}: {
  weddingNavKey: string | null;
  categoryFilter: WCategoryView[] | null;
  collectionKey?: string;
  brandKey?: string;
  isBaby?: boolean;
  userContext: UserContext | null;
}): GetNavPropsResult => {
  //
  // Get the primary link
  ///
  let activePrimaryLink: ShopPrimaryLink = weddingNavKey
    ? PrimaryNavIdentifier.WEDDING_BOUTIQUE
    : PrimaryNavIdentifier.REGISTRY;
  if (isBaby) {
    activePrimaryLink = PrimaryNavIdentifier.SHOP;
  }

  // Determine the secondary link:
  // - for category pages, its the category key
  // - For collections, its either new arrivals or collections, we split the new
  //   arrivals collection into its own nav link
  // - If we have a brand key, thats a brand page and we use that
  let activeSecondaryLink: SecondaryNavIdentifier | undefined =
    categoryFilter && categoryFilter.length
      ? (categoryFilter[0].key as SecondaryNavIdentifier)
      : undefined;

  if (!activeSecondaryLink && collectionKey) {
    if (isNewArrivalCollection(collectionKey)) {
      activeSecondaryLink = NEW_ARRIVALS;
    } else {
      activeSecondaryLink = COLLECTIONS;
    }
  }
  if (brandKey) activeSecondaryLink = BRANDS;

  // The tertiary link isn't used all the time.  For registry pages, we push
  // the secondary links down to tertiary links and introduce a new row in the
  // nave to shop / manage registry / track gifts. So by default, we push down
  // the secondary link into the tertiary.  And for the REGISTRY pages (only)
  // and if you are post-auth, we switch the secondary nav identifer based on
  // your wedding date.
  const activeTertiaryLink: SecondaryNavIdentifier | undefined = activeSecondaryLink;
  if (activePrimaryLink === PrimaryNavIdentifier.REGISTRY && !userContext?.is_guest) {
    activeSecondaryLink = isUserPostWedding(userContext ?? undefined)
      ? SecondaryNavIdentifier.POST_WEDDING_SHOP
      : SecondaryNavIdentifier.ADD_GIFTS;
  }

  return {
    activePrimaryLink,
    activeSecondaryLink,
    activeTertiaryLink,
  };
};

export const Header = (props: HeaderProps) => {
  const {
    aboveBannerContent,
    metaData,
    categoryFilter,
    weddingNavKey,
    showBanner = true,
    collectionKey,
    brandKey,
    isBaby,
    userContext,
    isPostAuthNav3Shown,
  } = props;

  return (
    <ShopHeader
      aboveBannerContent={aboveBannerContent}
      metaData={metaData}
      isBaby={isBaby}
      isWeddingBoutiquePage={weddingNavKey}
      showBanner={showBanner}
      isPostAuthNav3Shown={isPostAuthNav3Shown}
      {...getNavProps({
        brandKey,
        categoryFilter,
        collectionKey,
        isBaby,
        weddingNavKey,
        userContext,
      })}
    />
  );
};
